import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useRoutes, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { HelmetProvider } from 'react-helmet-async';
import { initGA, logPageView } from './config/analytics';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Container, 
  Chip,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Settings from '@mui/icons-material/Settings';
import Logout from '@mui/icons-material/Logout';
import { routes, navigationItems, adminNavigationItems, nonAuthenticatedNavigationItems } from './router/routes';
import { Footer } from './components/Footer';
import Logo from './assets/images/ship-compare-africa-logo.png';
import { RepositoryProvider } from './repositories/RepositoryProvider';
import { AuthProvider } from './contexts/AuthContext';
import { UserMenu } from './components/UserMenu';
import { useAuth } from './contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, signOut, userType } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    logPageView(location.pathname);
  }, [location]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
    handleDrawerToggle();
  };

  const allNavigationItems = [
    ...navigationItems,
    ...(user 
      ? (userType === 'Super Admin' ? adminNavigationItems : [])
      : nonAuthenticatedNavigationItems
    ),
  ];

  const mobileMenu = (
    <Drawer
      variant="temporary"
      anchor="right"
      open={mobileOpen}
      onClose={handleDrawerToggle}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        display: { xs: 'block', sm: 'none' },
        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
      }}
    >
      <List sx={{ pt: 8 }}>
        {allNavigationItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem 
              key={item.path}
              component={Link}
              to={item.path}
              onClick={handleDrawerToggle}
              sx={{
                color: isActive ? 'primary.main' : 'text.primary',
                backgroundColor: isActive ? 'action.selected' : 'transparent',
              }}
            >
              <ListItemText primary={item.label} />
            </ListItem>
          );
        })}
        {user && (
          <>
            <Divider sx={{ my: 2 }} />
            <ListItem 
              component={Link}
              to="/settings"
              onClick={handleDrawerToggle}
              sx={{
                color: location.pathname === '/settings' ? 'primary.main' : 'text.primary',
                backgroundColor: location.pathname === '/settings' ? 'action.selected' : 'transparent',
              }}
            >
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
            <ListItem 
              onClick={handleSignOut}
              sx={{
                color: 'text.primary',
              }}
            >
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              <ListItemText primary="Sign Out" />
            </ListItem>
          </>
        )}
      </List>
    </Drawer>
  );

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <AppBar position="fixed" elevation={0} sx={{ 
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid rgb(217, 217, 217)',
      }}>
        <Toolbar>
          <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, textDecoration: 'none' }}>
            <Box
              component="img"
              src={Logo}
              alt="ShipCompare Africa Logo"
              sx={{
                height: {
                  xs: '25px',
                  sm: '35px'
                },
                width: 'auto',
                mr: 1
              }}
            />
          </Box>
          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box display="flex" gap={1} alignItems="center">
              {allNavigationItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Chip
                    key={item.path}
                    label={item.label}
                    component={Link}
                    to={item.path}
                    clickable
                    color={isActive ? "primary" : "default"}
                    sx={{
                      backgroundColor: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.8)',
                      color: isActive ? 'white' : 'text.primary',
                      '&:hover': {
                        backgroundColor: isActive ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)',
                      },
                      fontWeight: 500,
                    }}
                  />
                );
              })}
              {user && <UserMenu />}
            </Box>
          )}
        </Toolbar>
      </AppBar>
      {isMobile && mobileMenu}
      <Toolbar />
      <Box component="main" sx={{ flex: 1 }}>
        <Container maxWidth="lg">
          <Box py={3}>{children}</Box>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

const AppRoutes: React.FC = () => {
  const element = useRoutes(routes);
  return element;
};

function App() {
  useEffect(() => {
    initGA();
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <AuthProvider>
            <RepositoryProvider>
              <Router>
                <Layout>
                  <AppRoutes />
                </Layout>
              </Router>
            </RepositoryProvider>
          </AuthProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
